import { DateTime } from 'luxon';
import { Mod } from '../model/tag';
import { Template } from '../model/template';

export const terminalTheme: Template = {
  tag: 'theme/terminal',
  name: $localize`🖌️ Terminal Theme`,
  config: {
    type: 'theme',
    mod: $localize`🖌️ Themes`,
    generated: $localize`Generated by jasper-ui ${DateTime.now().toISO()}`,
    description: $localize`Adds a terminal graphics style theme to the global theme list. Will combine
    themes if multiple specified with the same name.`,
    themes: {
      [$localize`Default`]:
      // language=CSS
      `
        body.dark-theme {
          --bg: #343434;
          --bg-accent: rgba(90, 90, 90, 0.05);
          --text: #c9c9c9;
          --active: #dc5c5c;
          --info: #969696;
          --tag: #af5800;
          --border: lightgrey;
          --border-accent: rgba(255, 255, 255, 0.4);
          --unselected-tab: grey;
          --error: #dc5c5c;
          --error-bg: #231010;
          --warning: #967f46;
          --mod: #4d9339;
          --card: #3d3d3d;
          --card-dragging: #4d4d4d;
          --placeholder: #2c2c2c;
          --deleting: #dc5c5c;
        }
        body.light-theme {
          --bg: white;
          --bg-accent: rgba(0, 0, 0, 0.05);
          --text: black;
          --blue: rgba(207, 221, 255, 0.7);
          --active: #da0000;
          --info: gray;
          --tag: #3d3dbb;
          --tag-accent: #f3ebc7;
          --border: black;
          --border-accent: rgba(0, 0, 0, 0.4);
          --border-light: rgba(0, 0, 0, 0.2);
          --unselected-tab: #b3b9c4;
          --error: #da0000;
          --error-bg: #ffa9a9;
          --warning: #c1c500;
          --mod: #136500;
          --card: #f3ebc7;
          --placeholder: #efebdf;
          --deleting: #ffa9a9;
        }
      `,
      [$localize`Terminal`]:
      // language=CSS
        `
          /*!
           *  Hack typeface https://github.com/source-foundry/Hack
           *  License: https://github.com/source-foundry/Hack/blob/master/LICENSE.md
           */
          /* FONT PATHS
           * -------------------------- */
          @font-face {
            font-family: 'Hack';
            src: url('assets/fonts/hack-regular.woff2?sha=3114f1256') format('woff2'), url('assets/fonts/hack-regular.woff?sha=3114f1256') format('woff');
            font-weight: 400;
            font-style: normal;
          }

          @font-face {
            font-family: 'Hack';
            src: url('assets/fonts/hack-bold.woff2?sha=3114f1256') format('woff2'), url('assets/fonts/hack-bold.woff?sha=3114f1256') format('woff');
            font-weight: 700;
            font-style: normal;
          }

          @font-face {
            font-family: 'Hack';
            src: url('assets/fonts/hack-italic.woff2?sha=3114f1256') format('woff2'), url('assets/fonts/hack-italic.woff?sha=3114f1256') format('woff');
            font-weight: 400;
            font-style: italic;
          }

          @font-face {
            font-family: 'Hack';
            src: url('assets/fonts/hack-bolditalic.woff2?sha=3114f1256') format('woff2'), url('assets/fonts/hack-bolditalic.woff?sha=3114f1256') format('woff');
            font-weight: 700;
            font-style: italic;
          }

          * {
            font-family: Hack, monospace;
          }

          .inbox {
            font-family: Arial, Helvetica, sans-serif;
          }

          body.dark-theme {
            --bg: #333;
            --tag: #5e8a9a;
            --mod: #b44125;
          }

          body.light-theme {
            --bg: #EEE;
          }

          .container,
          .tabs > a.current-tab {
            background: var(--bg) !important;
          }

          body.dark-theme, body.dark-theme ::backdrop, body.dark-theme .editor.fullscreen app-md {
            background-image: none;
            background-blend-mode: normal;
          }

          body.dark-theme .container {
            background: none;
          }

          body.light-theme, body.light-theme ::backdrop, body.light-theme .editor.fullscreen app-md {
            background-image: none;
            background-blend-mode: normal;
          }

          .sidebar details summary > span:after {
            content: '+';
          }

          .sidebar details[open] summary > span:after {
            content: '+';
            transform: rotate(-135deg);
          }
        `,
    },
  },
};

export const nightTheme: Template = {
  tag: 'theme/night',
  name: $localize`🖌️ Night Theme`,
  config: {
    type: 'theme',
    mod: $localize`🖌️ Themes`,
    generated: $localize`Generated by jasper-ui ${DateTime.now().toISO()}`,
    description: $localize`Adds a night theme to the global theme list.`,
    themes: {
      [$localize`Night`]:
      // language=CSS
        `
          body {
            color-scheme: dark !important;
            filter: grayscale(1) sepia(1) hue-rotate(90deg) !important;
            --bg: #000 !important;
            --bg-accent: rgba(90, 90, 90, 0.05);
            --bg-hover: rgba(255, 255, 255, 0.2);
            --bg-active: rgba(255, 255, 255, 0.4);
            --text: #0F0 !important;
            --visited: #c66acb;
            --link: #5769c2;
            --active: #dc5c5c;
            --info: #969696;
            --tag: #0F0 !important;
            --toggle: #525252;
            --toggle-accent: #777;
            --toggle-active: #888;
            --border: lightgrey;
            --border-accent: rgba(255, 255, 255, 0.4);
            --unselected-tab: grey;
            --error: #dc5c5c;
            --error-bg: #231010;
            --warning: #967f46;
            --mod: #0F0 !important;
            --card: #080808 !important;
            --card-dragging: #111 !important;
            --placeholder: rgba(44, 44, 44, 0.3) !important;
            --deleting: #dc5c5c;
          }

          body, body ::backdrop, body .editor.fullscreen app-md, .tabs {
            background-image: none !important;
            background-blend-mode: normal !important;
          }

          button, select {
            border: 1px solid #0F0;
            border-radius: 4px;
            background-color: black;
          }
        `,
    },
  },
};

export const themesMod: Mod = {
   template: [
    terminalTheme,
    nightTheme,
  ],
};
